// -----------------------------------------------------------------------------
// Library
// -----------------------------------------------------------------------------
import React, { useEffect, useState } from "react";
import { Field, Form } from "react-final-form";
import { Col, Modal, Row, Typography } from "antd";
import notification from "antd/lib/notification";

// -----------------------------------------------------------------------------
// Components and helpers
// -----------------------------------------------------------------------------
import InputField from "components/UI/Form/InputField";
import Label from "components/UI/Form/label";
import { validateEmailOrPhone, verifyPassword, validateEmail } from "helpers/Form/commonFormValidations";
import { isValidPhoneNumber } from "../../helpers/Form/commonFormValidations";
import { Paragraph9 } from "themes/default/_typography";
import { Button } from "components/Common/Button";
import { useNavigate, useParams } from "react-router";
import {
	useForgotPasswordMutation,
	useGetDomainGymQuery,
	useLoginMutation,
	useAcceptTermsOfUseMutation,
} from "store/api/api";
import { getClientHost } from "helpers/constants";
import { useSetRecoilState } from "recoil";
import { userAtom } from "globalAtoms";
import { getUser } from "useInit";
import { useLoginProgress, setLoginInProgress } from "utils/loginInProgress";
import VerifyPhoneNumber from "./VerifyPhoneNumber";
import TermsOfServiceModal from "pages/TermsOfService";

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------
const LoginForm = ({ preview, onLoginSuccess }) => {
	const navigate = useNavigate();
	const { gym } = useParams();
	const [loggingIn, updateLoggingIn] = useState(false);
	const [displayModal, setDisplayModal] = useState(false);
	const [isGymReady, setIsGymReady] = useState(true);
	const [termsChecked, setTermsChecked] = useState(false);
	const [acceptTermsOfUse] = useAcceptTermsOfUseMutation();
	//TDO should be gym domain
	const { data: domainGymData } = useGetDomainGymQuery(gym);
	const [resetPasswordTrigger, resetPasswordResponse] = useForgotPasswordMutation();
	const [loginTrigger, loginResponse] = useLoginMutation();
	const [isDisabledLoginBtn, setDisabledLoginBtn] = useState(false);
	const setUser = useSetRecoilState(userAtom);
	const [verifyPhone, setVerifyPhone] = useState(false);
	const { inProgress } = useLoginProgress();

	const handleFormSubmit = async (formObj) => {
		if (inProgress) {
			return notification.error({ message: "Another login in progress in another tab. Please wait." });
		}
		setLoginInProgress(true);
		if (!preview && !loggingIn) {
			updateLoggingIn(true);
			const keyName = isValidPhoneNumber(formObj.email) ? "phone_number" : "email_address";
			const loginResponse = await loginTrigger({
				[keyName]: formObj.email,
				password: formObj.password,
				domain: domainGymData.subdomain,
			});
			handlePostLogin(loginResponse);
			updateLoggingIn(false);
		}
		updateLoggingIn(false);
	};

	const handleNav = async (data) => {
		const authToken = data?.auth_token;
		localStorage.setItem("creator_jwt_token", authToken);
		await getUser(setUser);
		if (termsChecked) {
			await acceptTermsOfUse().unwrap();
		}
		navigate(`/${domainGymData?.subdomain}/dashboard/`);
	};

	const handlePostLogin = (loginResponse) => {
		if (loginResponse.data) {
			const { user } = loginResponse.data;
			if (user) {
				if (user?.is_phone_number_valid) {
					setVerifyPhone(true);
				} else {
					notification.success({ message: "Login Successful" });
					handleNav(loginResponse.data);
				}
				onLoginSuccess?.();
			} else {
				notification.error({
					message: "An Error occurred during login",
				});
				setLoginInProgress(false);
			}
		}
		if (loginResponse.error) {
			setDisabledLoginBtn(false);
			if (loginResponse?.error?.status === 404) {
				if (loginResponse?.error?.data?.message === "User Not Found") {
					notification.error({
						message: "Incorrect email/phone or password entered",
					});
				} else {
					setIsGymReady(false);
				}
			} else {
				notification.error({
					message: "Incorrect email/phone or password entered",
				});
			}
			setLoginInProgress(false);
		}
	};

	const handleForgotPassword = () => {
		setDisplayModal(true);
	};

	const handleSendPassword = async ({ email }) => {
		try {
			const domain = getClientHost();
			await resetPasswordTrigger({ email, domain, gym: gym }).unwrap();
		} catch (error) {
			console.log("could not reset password", error);
		}
	};

	useEffect(() => {
		if (resetPasswordResponse.isSuccess) {
			notification.success({
				message: "Password reset email sent",
			});
			setDisplayModal(false);
		} else if (resetPasswordResponse.isError) {
			if (resetPasswordResponse.error.status === 429) {
				notification.error({
					message: "Sorry, we are unable to reset your password at this moment. Please try again later.",
				});
			} else {
				notification.error({
					message:
						"Sorry, we could not find a user with this email address. Please check your email address and try again.",
				});
			}
		}
	}, [resetPasswordResponse]);

	const { user, auth_token: token } = loginResponse?.data ?? {};

	useEffect(() => {
		let timer;
		if (loginResponse.isLoading) {
			setDisabledLoginBtn(true);
			timer = setTimeout(() => {
				setDisabledLoginBtn(false);
			}, 1000);
		}
		return clearTimeout(timer);
	}, [loginResponse]);

	return (
		<div className="sb-login-form" data-id="frmLogin">
			<>
				{!isGymReady && <Label>This creator is still setting up their gym, check back soon!</Label>}
				{isGymReady && (
					<>
						{!verifyPhone && (
							<Form onSubmit={handleFormSubmit}>
								{({ handleSubmit }) => {
									return (
										<form id="login" onSubmit={handleSubmit}>
											<Row gutter={[8, 16]}>
												<Col span={24}>
													<Label>Phone Number Or Email Address</Label>
													<Field component="input" name="email" validate={validateEmailOrPhone}>
														{({ input, meta }) => (
															<InputField
																disabled={preview}
																type="text"
																input={input}
																meta={meta}
																data-id="txtEmail"
																size="small"
																placeholder={"Phone Number or Email Address"}
															/>
														)}
													</Field>
												</Col>
												<Col span={24}>
													<Label>Password</Label>
													<Field component="input" name="password" validate={verifyPassword}>
														{({ input, meta }) => (
															<InputField
																disabled={preview}
																type="password"
																input={input}
																meta={meta}
																data-id="txtPassword"
																placeholder={"Password"}
															/>
														)}
													</Field>
												</Col>
												<Col span={24}></Col>
											</Row>
											<div className="sb-gym-login-form">
												<label
													className="sb-gym-login-form__forgot"
													data-id="lblForgotPassword"
													onClick={() => handleForgotPassword()}
												>
													Forgot Password?
												</label>
											</div>
											<div className="sb-login-form__buttons">
												<Button
													style={{
														borderColor: domainGymData?.brand_color1 !== "" ? domainGymData?.brand_color1 : null,
														padding: 0,
													}}
													className="primary-filled"
													data-id="btnLogin"
													uppercase
													type="submit"
													disabled={isDisabledLoginBtn}
													loading={isDisabledLoginBtn}
												>
													<Paragraph9>{isDisabledLoginBtn ? "Logging In" : "Login"}</Paragraph9>
												</Button>
												<Button
													disabled={isDisabledLoginBtn}
													data-id="btnSignUp"
													className="primary-filled--branding-primary-color"
													uppercase
													type="button"
													onClick={() => navigate(`../signup/${domainGymData?.subdomain}`)}
													style={{
														padding: 0,
														backgroundColor: domainGymData?.brand_color1 !== "" ? domainGymData?.brand_color1 : null,
													}}
												>
													<Paragraph9>Sign Up</Paragraph9>
												</Button>
											</div>
											<div style={{ marginTop: 8 }}>
												<Typography.Text>By logging in to SWEATBASE you agree to our </Typography.Text>
												<TermsOfServiceModal
													checked={termsChecked}
													setChecked={setTermsChecked}
													text="Terms of Service"
												/>
											</div>
										</form>
									);
								}}
							</Form>
						)}
						{verifyPhone && <VerifyPhoneNumber onContinue={handleNav} token={token} user={user} />}
					</>
				)}
			</>
			<Modal
				title="Forgot Password"
				style={{ display: "flex", justifyContent: "center" }}
				open={displayModal}
				onCancel={() => setDisplayModal(false)}
				footer={null}
				maskClosable={false}
				destroyOnClose
			>
				<Form onSubmit={handleSendPassword}>
					{({ handleSubmit }) => {
						return (
							<form onSubmit={handleSubmit}>
								<Field name="email" validate={validateEmail}>
									{({ input, meta }) => (
										<InputField
											disabled={preview}
											type="text"
											input={input}
											meta={meta}
											placeholder="Enter Email Address"
											data-id="txtEmail"
											style={{ width: "100%" }}
										/>
									)}
								</Field>
								<Button
									style={{ marginTop: 16, maxWidth: "100%" }}
									data-id="sb-forgot-password-send-button"
									className="primary-filled"
									key="Send"
									type="submit"
								>
									Send
								</Button>
							</form>
						);
					}}
				</Form>
			</Modal>
		</div>
	);
};

export default LoginForm;
